// Shared helpers for client approval workflows.
// Keep this logic consistent across Client Portal, Project Detail, Admin, and command center.

function safeStr(x) {
  return (x ?? "").toString();
}

/**
 * Normalize pmProjectFiles.reviewStatus to snake_case (no spaces/hyphens).
 * @param {object|string|null|undefined} fileMetaOrStatus
 * @returns {string} empty when unset
 */
export function normalizeReviewStatus(fileMetaOrStatus) {
  const raw =
    typeof fileMetaOrStatus === "object" && fileMetaOrStatus !== null
      ? fileMetaOrStatus.reviewStatus
      : fileMetaOrStatus;
  return safeStr(raw).toLowerCase().trim().replace(/\s+/g, "_").replace(/-+/g, "_");
}

/** CL-385B: only these statuses mean the client must formally respond. */
export const CLIENT_ACTION_REQUIRED_STATUSES = Object.freeze(["pending_client_review"]);

/**
 * Returns whether the file is in a persisted "client must act" review state.
 *
 * CL-385B v1 rules:
 * - No reviewStatus → visible/shared only, not action required.
 * - Team-queue states (in_review, under_review, submitted, resubmitted) → not client action.
 * - Post-response states (approved, revisions_required, changes_requested, closed, no_action, …) → not client action.
 * - Only pending_client_review counts until explicit review-request workflow (CL-385C+).
 */
export function needsClientApproval(fileMeta) {
  const f = fileMeta || {};
  if (safeStr(f.status).toLowerCase() === "deleted") return false;

  const s = normalizeReviewStatus(f);
  if (!s) return false;

  return CLIENT_ACTION_REQUIRED_STATUSES.includes(s);
}

/**
 * Returns pending approvals for a given file list.
 */
export function getPendingApprovals(files) {
  const list = Array.isArray(files) ? files : [];
  return list.filter((f) => needsClientApproval(f));
}

/**
 * Sort approvals by most recently updated (fallback to createdAt).
 */
export function sortApprovalsByRecent(list) {
  const arr = (Array.isArray(list) ? list : []).slice();
  const toMs = (d) => {
    if (!d) return 0;
    if (typeof d?.toDate === "function") return d.toDate().getTime();
    const t = new Date(d).getTime();
    return Number.isFinite(t) ? t : 0;
  };
  arr.sort((a, b) => {
    const am = toMs(a.updatedAt) || toMs(a.createdAt);
    const bm = toMs(b.updatedAt) || toMs(b.createdAt);
    return bm - am;
  });
  return arr;
}
